import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Plus, Send } from "lucide-react";

import { ModelSelector } from "./ModelSelector";
import { Stepper } from "./StartPage";

const LOADING_LINES = [
  "Breaking down your feature…",
  "Identifying where AI is actually needed…",
  "Estimating costs…",
];

export const Phase2Conversation = ({
  session,
  models,
  model,
  setModel,
  onSendChip,
  onSendText,
  onAnalyze,
  onNewFeature,
  busy,
  thinking,
  analyzing,
  showAnalyzeCta,
  retryDraft,
  clearRetryDraft,
  voice,
  children,
}) => {
  const [draft, setDraft] = useState("");
  const [loadingIdx, setLoadingIdx] = useState(0);
  const scrollRef = useRef(null);
  const messages = session?.conversation || [];
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const lastOptions = lastAssistant?.options || [];
  const waitingForUser = !busy && messages.length > 0 && messages[messages.length - 1].role === "assistant";

  // If a previous send failed, restore the message into the composer.
  useEffect(() => {
    if (retryDraft) {
      setDraft(retryDraft);
      clearRetryDraft?.();
    }
  }, [retryDraft, clearRetryDraft]);

  // Cycle through loading copy while /analyze is running.
  useEffect(() => {
    if (!analyzing) {
      setLoadingIdx(0);
      return undefined;
    }
    setLoadingIdx(0);
    const t1 = setTimeout(() => setLoadingIdx(1), 800);
    const t2 = setTimeout(() => setLoadingIdx(2), 1600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [analyzing]);

  // Voice integration: when the user stops speaking, send the transcript.
  useEffect(() => {
    if (voice?.transcript && !voice.listening) {
      const t = voice.transcript.trim();
      if (t) {
        voice.reset?.();
        onSendText(t);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice?.listening]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, thinking, analyzing]);

  const submit = (e) => {
    e?.preventDefault();
    const t = draft.trim();
    if (!t || busy) return;
    setDraft("");
    onSendText(t);
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="phase-2-conversation">
      {/* Header */}
      <div className="border-b border-[#DDD9CF] sticky top-0 z-20 bg-[#F9F7F3]">
        <div className="max-w-3xl xl:max-w-5xl 2xl:max-w-6xl mx-auto px-6 md:px-12 xl:px-16 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="font-serif-display text-xl text-[#1A1814] select-none" data-testid="header-title">
            SuperHumanly
          </div>
          <Stepper activeStep={session?.analyses && Object.keys(session.analyses).length > 0 ? 3 : 2} />
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={onNewFeature}
              className="text-sm text-[#56534A] hover:text-[#C4521E] transition-colors flex items-center gap-1.5"
              data-testid="new-feature-button"
            >
              <Plus size={14} strokeWidth={1.5} />
              New feature
            </button>
            <ModelSelector models={models} value={model} onChange={setModel} disabled={busy || analyzing} />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className="max-w-3xl xl:max-w-5xl 2xl:max-w-6xl mx-auto px-6 md:px-12 xl:px-16 py-10 space-y-8 pb-40">
          <AnimatePresence initial={false}>
            {messages.map((m, i) => {
              const isLast = i === messages.length - 1;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className={m.role === "assistant" ? "pr-12" : "pl-12 md:pl-24"}
                  data-testid={`message-${m.role}-${i}`}
                >
                  {m.role === "assistant" ? (
                    <div className="max-w-3xl">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#56534A] mb-2">
                        SuperHumanly
                      </div>
                      <p className="text-[#1A1814] text-base md:text-lg leading-relaxed font-serif-display">
                        {m.content}
                      </p>
                      {/* Render chips only for the latest assistant message that is still awaiting an answer */}
                      {isLast && waitingForUser && m.options?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="option-chips">
                          {m.options.map((opt, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => onSendChip(opt)}
                              disabled={busy}
                              className="text-sm px-4 py-2 border border-[#DDD9CF] hover:border-[#C4521E] hover:text-[#C4521E] text-[#1A1814] rounded-full transition-colors disabled:opacity-40"
                              data-testid={`option-chip-${idx}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-l border-[#DDD9CF] pl-5">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#56534A] mb-2">You</div>
                      <p className="text-[#56534A] text-base leading-relaxed whitespace-pre-wrap">{m.content}</p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {thinking && (
            <div className="pr-12 flex items-center gap-1.5" data-testid="thinking-indicator">
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span className="thinking-dot" />
            </div>
          )}

          {analyzing && (
            <div className="pr-12 space-y-2" data-testid="analyzing-indicator">
              {LOADING_LINES.slice(0, loadingIdx + 1).map((line, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-[#56534A] italic font-serif-display text-base"
                >
                  {line}
                </motion.p>
              ))}
            </div>
          )}

          {showAnalyzeCta && !analyzing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="pt-4"
              data-testid="analyze-cta-wrapper"
            >
              <button
                type="button"
                onClick={onAnalyze}
                disabled={busy || analyzing}
                className="bg-[#C4521E] text-[#F9F7F3] px-8 py-3 font-medium tracking-wide hover:bg-[#a34418] transition-colors disabled:opacity-40"
                data-testid="analyze-button"
              >
                See the full picture
              </button>
            </motion.div>
          )}

          {/* Phase 3 output renders inline here so the page scrolls naturally */}
          {children}
        </div>
      </div>

      {/* Composer (custom text + voice). Hidden while analyzing. */}
      {!analyzing && (
        <div className="border-t border-[#DDD9CF] bg-[#F9F7F3]">
          <form onSubmit={submit} className="max-w-3xl xl:max-w-5xl 2xl:max-w-6xl mx-auto px-6 md:px-12 xl:px-16 py-4 flex items-end gap-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  submit(e);
                }
              }}
              placeholder={
                lastOptions.length > 0 && waitingForUser
                  ? "…or type your own answer"
                  : busy
                  ? "Hold on…"
                  : "Type your answer. Press Enter to send."
              }
              rows={1}
              disabled={busy}
              className="flex-1 bg-transparent border-b border-[#DDD9CF] focus:border-[#C4521E] outline-none resize-none text-[#1A1814] placeholder-[#56534A]/70 py-2 leading-relaxed text-sm"
              data-testid="chat-input"
            />
            {voice?.supported && (
              <button
                type="button"
                onClick={voice.toggle}
                disabled={busy}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                  voice.listening
                    ? "bg-[#C4521E] border-[#C4521E] text-[#F9F7F3] mic-listening"
                    : "border-[#DDD9CF] text-[#56534A] hover:text-[#C4521E]"
                } disabled:opacity-40`}
                data-testid="chat-mic"
                aria-label={voice.listening ? "Stop recording" : "Speak instead"}
              >
                <Mic size={15} strokeWidth={1.5} />
              </button>
            )}
            <button
              type="submit"
              disabled={!draft.trim() || busy}
              className="text-[#C4521E] hover:text-[#1A1814] transition-colors disabled:opacity-30"
              data-testid="chat-send"
              aria-label="Send"
            >
              <Send size={18} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
